import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

const PrivacyPolicy = () => {
  return (
    <div data-testid="privacy-policy-page" className="min-h-screen bg-[#0B0D10] text-[#A6AFBD]">
      <div className="max-w-[800px] mx-auto px-6 lg:px-12 py-20 lg:py-28">
        <Link
          to="/"
          data-testid="privacy-back-link"
          className="inline-flex items-center gap-2 text-sm text-[#A6AFBD]/60 hover:text-[#F2F4F7] transition-colors mb-12"
        >
          <ArrowLeft size={16} />
          Back to Home
        </Link>

        <h1 className="text-4xl sm:text-5xl font-semibold text-[#F2F4F7] leading-tight tracking-tight mb-2">
          Privacy Policy
        </h1>
        <p className="text-base text-[#A6AFBD]/60 mb-4">ramongovea.com</p>
        <p className="text-sm text-[#A6AFBD]/40 mb-12">
          Effective Date: March 2026 | Last Updated: April 2026
        </p>

        <div className="space-y-10 text-base leading-relaxed">
          <p>
            Your privacy matters. This Privacy Policy explains what information we collect when you visit ramongovea.com, purchase access to IP Builder's Academy, or interact with our community — and how we use, protect, and handle that information.
          </p>
          <p>
            By using this site or purchasing any product, you agree to the terms of this policy.
          </p>

          <Section num="1" title="Who We Are">
            <p>IP Builder's Academy is operated by Ramon Govea under Twin Flame Productions.</p>
            <p>Contact: <a href="mailto:support@ramongovea.com" className="text-[#3B82F6] hover:underline">support@ramongovea.com</a></p>
          </Section>

          <Section num="2" title="Information We Collect">
            <Subsection title="When You Purchase">
              <p>When you buy a course tier (Foundation, Pro, or VIP), we collect:</p>
              <ul className="list-disc pl-6 space-y-1 mt-2">
                <li>Name and email address</li>
                <li>Billing information (processed securely by Stripe — we do not store your card details)</li>
                <li>Purchase history and tier selection</li>
              </ul>
            </Subsection>
            <Subsection title="When You Join the Community">
              <p>When you access the Skool community included with your purchase, Skool's own privacy policy governs your data within that platform. We receive basic profile information to verify your enrollment.</p>
            </Subsection>
            <Subsection title="When You Visit the Website">
              <p>We may collect standard analytics data including:</p>
              <ul className="list-disc pl-6 space-y-1 mt-2">
                <li>Pages visited and time on site</li>
                <li>Browser type and device</li>
                <li>Referring URLs</li>
              </ul>
              <p className="mt-3">This data is aggregated and anonymous. It helps us improve the site and understand what content is most useful.</p>
            </Subsection>
            <Subsection title="When You Contact Us">
              <p>If you email support@ramongovea.com, we retain your name, email address, and the content of your message to respond to your inquiry and maintain records of any support interactions.</p>
            </Subsection>
          </Section>

          <Section num="3" title="How We Use Your Information">
            <p>We use the information we collect to:</p>
            <ul className="list-disc pl-6 space-y-1 mt-2">
              <li>Process your purchase and grant access to the course and community</li>
              <li>Send order confirmation and access instructions</li>
              <li>Respond to support requests</li>
              <li>Send course updates, new modules, or relevant offers (you may opt out at any time)</li>
              <li>Improve our website, content, and offerings based on usage patterns</li>
            </ul>
            <p className="mt-4 font-medium text-[#F2F4F7]">We do not sell, rent, or trade your personal information to third parties. Ever.</p>
          </Section>

          <Section num="4" title="Payment Processing">
            <p>All payments are processed through Stripe. We do not store, see, or have access to your full credit card number, CVV, or banking details. Stripe's privacy policy governs how your payment data is handled. You can review it at <a href="https://stripe.com/privacy" target="_blank" rel="noopener noreferrer" className="text-[#3B82F6] hover:underline">stripe.com/privacy</a>.</p>
          </Section>

          <Section num="5" title="Course Platform (Skool)">
            <p>IP Builder's Academy is hosted on Skool. When you access the course and community, Skool collects and processes data according to their own privacy policy. We recommend reviewing it at <a href="https://skool.com/privacy" target="_blank" rel="noopener noreferrer" className="text-[#3B82F6] hover:underline">skool.com/privacy</a>. We are not responsible for Skool's data practices.</p>
          </Section>

          <Section num="6" title="Email Communications">
            <p>When you purchase, you'll receive transactional emails related to your order and course access. You may also receive occasional emails about new content, community updates, or relevant offers.</p>
            <p className="mt-3">You can unsubscribe from marketing emails at any time using the unsubscribe link in any email. Transactional emails (receipts, access confirmations) cannot be opted out of as they are necessary to deliver your purchase.</p>
          </Section>

          <Section num="7" title="Cookies">
            <p>This site may use cookies to maintain session data, remember preferences, and analyze site traffic. You can disable cookies in your browser settings, though some site features may not function correctly without them.</p>
            <p className="mt-3">We do not use cookies to track you across third-party websites or serve targeted advertising.</p>
          </Section>

          <Section num="8" title="Data Retention">
            <p>We retain your purchase and account information for as long as necessary to provide your access and comply with legal obligations. If you request deletion of your data, we will remove what we are able to within 30 days, subject to any legal or contractual requirements to retain certain records.</p>
          </Section>

          <Section num="9" title="Your Rights">
            <p>You have the right to:</p>
            <ul className="list-disc pl-6 space-y-1 mt-2">
              <li>Request a copy of the personal data we hold about you</li>
              <li>Request correction of inaccurate data</li>
              <li>Request deletion of your data (subject to legal retention requirements)</li>
              <li>Opt out of marketing communications at any time</li>
            </ul>
            <p className="mt-3">To exercise any of these rights, email <a href="mailto:support@ramongovea.com" className="text-[#3B82F6] hover:underline">support@ramongovea.com</a> with your request. We will respond within 30 days.</p>
          </Section>

          <Section num="10" title="Children's Privacy">
            <p>IP Builder's Academy is intended for adults 18 years of age and older. We do not knowingly collect personal information from anyone under 18. If we become aware that a minor has submitted personal information, we will delete it promptly.</p>
          </Section>

          <Section num="11" title="Security">
            <p>We take reasonable measures to protect your information from unauthorized access, disclosure, or misuse. Payment data is handled entirely by Stripe using industry-standard encryption. However, no method of transmission over the internet is 100% secure, and we cannot guarantee absolute security.</p>
          </Section>

          <Section num="12" title="Third-Party Links">
            <p>Our site and course materials may contain links to third-party tools, platforms, or resources. We are not responsible for the privacy practices of those sites and encourage you to review their policies before providing any personal information.</p>
          </Section>

          <Section num="13" title="Changes to This Policy">
            <p>We may update this Privacy Policy from time to time. When we do, we will update the effective date at the top of this page. Continued use of our site or services after any changes constitutes your acceptance of the updated policy.</p>
          </Section>

          <Section num="14" title="Contact">
            <p>If you have any questions about this Privacy Policy or how your data is handled, contact us at:</p>
            <p className="mt-2">
              <a href="mailto:support@ramongovea.com" className="text-[#3B82F6] hover:underline">support@ramongovea.com</a>
            </p>
            <p className="mt-3">We typically respond within 2 business days.</p>
          </Section>
        </div>
      </div>
    </div>
  );
};

const Section = ({ num, title, children }) => (
  <div>
    <h2 className="text-lg font-semibold text-[#F2F4F7] mb-3">
      {num}. {title}
    </h2>
    <div className="space-y-3">{children}</div>
  </div>
);

const Subsection = ({ title, children }) => (
  <div className="mt-4">
    <h3 className="text-base font-medium text-[#F2F4F7]/80 mb-2">{title}</h3>
    {children}
  </div>
);

export default PrivacyPolicy;
